if (!customElements.get('mshq-custom-configurator')) {
  customElements.define(
    'mshq-custom-configurator',
    class MshqCustomConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';

        this.variants = this.readJson('[data-custom-variants]');
        this.optionInputs = [...this.querySelectorAll('[data-custom-option]')];
        this.sizeInputs = this.optionInputs.filter((input) => input.dataset.customOption === '1');
        this.textInput = this.querySelector('[data-custom-text]');
        this.textField = this.querySelector('[data-phrase-input-field]');
        this.fixedForm = this.querySelector('[data-fixed-form]');
        this.fixedPurchase = this.querySelector('[data-fixed-purchase]');
        this.quoteForm = this.querySelector('[data-quote-form]');
        this.variantId = this.querySelector('[data-custom-variant-id]');
        this.next = this.querySelector('[data-custom-next]');
        this.submit = this.querySelector('[data-custom-submit]');
        this.price = this.querySelector('[data-custom-price]');
        this.cartError = this.querySelector('[data-custom-cart-error]');
        this.customSizeToggle = this.querySelector('[data-custom-size-toggle]');
        this.customWidth = this.querySelector('[data-custom-width]');
        this.customHeight = this.querySelector('[data-custom-height]');
        this.preview = this.querySelector('[data-custom-preview]');
        this.sizePreview = this.querySelector('[data-custom-size]');
        this.paper = this.querySelector('[data-custom-paper]');
        this.frame = this.querySelector('[data-custom-frame]');
        this.decoratedPreview = this.querySelector('[data-decorated-preview]');
        this.decoratedSurface = this.querySelector('[data-decorated-surface]');
        this.decoratedPreviewText = this.querySelector('[data-decorated-preview-text]');
        this.frameColorProperty = this.querySelector('[data-frame-color-property]');
        this.previewWrap = this.querySelector('.mshq-custom__preview-wrap');
        this.roomFrame = this.querySelector('[data-room-frame]');
        this.roomPaper = this.querySelector('[data-room-paper]');
        this.roomText = this.querySelector('[data-room-text]');
        this.textError = this.querySelector('[data-text-error]');
        this.phraseCategoryProperty = this.querySelector('[data-phrase-category-property]');
        this.phraseSourceProperty = this.querySelector('[data-phrase-source-property]');
        this.currentCategory = this.querySelector('[data-phrase-category-select]')?.value || 'custom';
        this.customDraft = this.textInput?.value || '';
        this.customMode = false;
        this.selectedPhraseSource = 'custom';
        this.frameSpecs = this.readJson('[data-frame-specs]');
        this.frameImages = this.readJson('[data-frame-images]');
        this.frameTextures = this.readJson('[data-frame-textures]');
        this.frameColors = ['wood-brown', 'light-wood', 'striped-wood', 'black-gold', 'black-matte', 'white', 'gold'];
        this.frameSpecs.forEach((spec) => {
          spec.colors = [...this.frameColors];
          spec.backings = [...new Set([...(spec.backings || []), 'maroon'])];
          spec.mounts = ['rear-wood-stand', 'separate-wood-stand', 'wall-mount'];
          spec.woodStand = false;
        });
        this.frameSpec = null;
        this.frameShape = 'rectangle';
        this.customSizeMode = false;
        this.frameChoices = { color: 'black-gold', backing: '', paper: 'white', mount: '', woodStand: false };

        this.colorMap = {
          ivory: '#f5f1e6',
          beige: '#e8d5a9',
          gray: '#e4e4e1',
          black: '#1b1b1b',
          gold: '#b08d4f',
          walnut: '#5b3a24',
          wood: '#5b3a24',
          metal: '#141414',
          white: '#f7f5ef',
          'black-gold': '#1b1b1b',
          'black-matte': '#222222',
          'black-beveled': '#171717',
          'wood-brown': '#6b452c',
          'light-wood': '#ba8b62',
          'striped-wood': '#805b3c',
          maroon: '#800020',
          decorated: '#e7d2a6',
          transparent: 'rgba(255, 255, 255, 0.24)',
        };

        this.choiceLabels =
          this.dataset.locale === 'ar'
            ? {
                'wood-brown': 'بني خشبي داكن',
                'light-wood': 'بني فاتح',
                'striped-wood': 'بني مخطط',
                maroon: 'عنابي',
                'black-gold': 'أسود بحافة ذهبية',
                'black-matte': 'أسود',
                'black-beveled': 'أسود محفور',
                white: 'أبيض',
                transparent: 'شفاف',
                cardboard: 'كرتون',
                paper: 'ورق',
                decorated: 'مزخرف',
                beige: 'بيج',
                gray: 'رمادي',
                gold: 'ذهبي',
                'rear-wood-stand': 'ستاند خشبي خلفي',
                'separate-wood-stand': 'ستاند خشبي منفصل',
                'wall-mount': 'تعليق حائط',
              }
            : {
                'wood-brown': 'Dark wood brown',
                'light-wood': 'Light wood',
                'striped-wood': 'Striped wood',
                maroon: 'Maroon',
                'black-gold': 'Black with gold trim',
                'black-matte': 'Black',
                'black-beveled': 'Beveled black',
                white: 'White',
                transparent: 'Transparent',
                cardboard: 'Cardboard',
                paper: 'Paper',
                decorated: 'Decorated',
                beige: 'Beige',
                gray: 'Gray',
                gold: 'Gold',
                'rear-wood-stand': 'Rear wooden stand',
                'separate-wood-stand': 'Separate wooden stand',
                'wall-mount': 'Wall hanging',
              };

        this.defaultRules = [
          { size: '30x15', maxCharacters: 40, maxWords: 8, maxLines: 2 },
          { size: '40x20', maxCharacters: 60, maxWords: 12, maxLines: 3 },
          { size: '50x25', maxCharacters: 85, maxWords: 18, maxLines: 4 },
          { size: '60x30', maxCharacters: 110, maxWords: 24, maxLines: 4 },
        ];
        this.sizeRules = this.readSizeRules();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.fixedForm?.addEventListener('submit', this.addToCart.bind(this));
        this.quoteForm?.addEventListener('submit', this.prepareQuote.bind(this));
        this.querySelector('[data-custom-quote-panel]')?.addEventListener('toggle', this.onCustomQuoteToggle.bind(this));

        this.selectCategory('custom');

        if (this.frameSpecs.length) this.selectFrameModel(this.frameSpecs[0].number);
        this.setCustomizerTab('frame');

        if (this.querySelector('.form-status') || this.dataset.quoteSuccess === 'true') {
          this.setCustomMode(true);
        }

        this.initializeRoom();
        this.update();

        // Initial layout can be hidden or still resizing when the first fit runs.
        // Refit once the paper has its real dimensions and Arabic fonts are ready.
        const refitText = () => {
          if (!this.isConnected) return;
          const dimensions = this.currentDimensions();
          [this.preview, this.roomText, this.decoratedPreviewText].forEach((node) =>
            this.fitPreviewText(node, dimensions),
          );
        };
        this.textResizeObserver?.disconnect();
        this.textResizeObserver = new ResizeObserver(refitText);
        [this.paper, this.roomPaper, this.decoratedSurface].filter(Boolean).forEach((paper) =>
          this.textResizeObserver.observe(paper),
        );
        document.fonts?.ready.then(refitText);
      }

      readJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return [];
        try {
          return JSON.parse(node.textContent || '[]');
        } catch (_error) {
          return [];
        }
      }

      readSizeRules() {
        const rules = [...this.querySelectorAll('[data-size-rule]')].map((node) => ({
          size: this.normalizeSize(node.dataset.size),
          maxCharacters: Number(node.dataset.maxCharacters) || 40,
          maxWords: Number(node.dataset.maxWords) || 8,
          maxLines: Number(node.dataset.maxLines) || 2,
          guidance: node.dataset.guidance || '',
        }));
        return rules.length ? rules : this.defaultRules;
      }

      normalizeSize(value) {
        const dimensions = this.dimensionsFrom(value);
        return dimensions
          ? `${dimensions.width}x${dimensions.height}`
          : String(value || '')
              .trim()
              .toLowerCase();
      }

      dimensionsFrom(value) {
        const matches = String(value || '')
          .replace(/,/g, '.')
          .match(/\d+(?:\.\d+)?/g);
        if (!matches || matches.length < 2) return null;
        const width = Number(matches[0]);
        const height = Number(matches[1]);
        return width > 0 && height > 0 ? { width, height } : null;
      }

      selectedInput(position) {
        return this.optionInputs.find((input) => input.dataset.customOption === String(position) && input.checked);
      }

      selectedVariant() {
        const selected = [];
        this.optionInputs.forEach((input) => {
          if (input.checked) selected[Number(input.dataset.customOption) - 1] = input.value;
        });
        return (
          this.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null
        );
      }

      selectedPricingVariant() {
        if (this.frameSpec?.variantId) {
          const pricingVariant = this.variants.find((variant) => String(variant.id) === String(this.frameSpec.variantId));
          if (pricingVariant) return pricingVariant;
        }
        return this.selectedVariant();
      }

      onClick(event) {
        const customTab = event.target.closest('[data-custom-tab]');
        if (customTab) {
          this.setCustomizerTab(customTab.dataset.customTab);
          return;
        }

        if (event.target.closest('[data-custom-next]')) {
          this.advanceCustomizerTab();
          return;
        }

        const category = event.target.closest('[data-phrase-category]');
        if (category) {
          this.selectCategory(category.dataset.phraseCategory);
          return;
        }

        const phraseCard = event.target.closest('[data-phrase-card]');
        if (phraseCard) {
          if (phraseCard.getAttribute('aria-disabled') === 'true') return;
          this.selectPhrase(phraseCard);
          return;
        }

        if (event.target.closest('[data-custom-size-toggle]')) {
          this.setCustomMode(true);
          return;
        }

        const shape = event.target.closest('[data-frame-shape]');
        if (shape) {
          this.selectFrameShape(shape.dataset.frameShape);
          return;
        }

        const frameChoice = event.target.closest('[data-frame-choice]');
        if (frameChoice) {
          this.frameChoices[frameChoice.dataset.frameChoice] = frameChoice.dataset.value;
          this.renderFrameSpecControls();
          this.update();
          return;
        }



        if (event.target.closest('[data-wood-stand]')) {
          this.frameChoices.woodStand = Boolean(this.querySelector('[data-wood-stand]')?.checked);
          this.update();
          return;
        }

        const previewTab = event.target.closest('[data-preview-tab]');
        if (previewTab) {
          this.selectPreview(previewTab.dataset.previewTab);
          return;
        }

        if (event.target.closest('[data-ai-submit]')) this.generateAiPreview();
      }

      onInput(event) {
        if (event.target === this.textInput && this.selectedPhraseSource === 'custom') {
          this.customDraft = this.textInput.value;
        }
        this.update();
      }

      setCustomizerTab(tab) {
        const tabs = ['frame', 'background', 'display', 'phrase'];
        const activeTab = tabs.includes(tab) ? tab : 'frame';
        const useMobileStepFlow = window.matchMedia('(max-width: 719px)').matches;
        this.querySelectorAll('[data-custom-tab]').forEach((button) => {
          const active = button.dataset.customTab === activeTab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        this.querySelectorAll('[data-custom-tab-panel]').forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.customTabPanel === activeTab);
        });
        if (this.next) this.next.hidden = this.customMode || !useMobileStepFlow || activeTab === tabs[tabs.length - 1];
        if (this.submit) this.submit.hidden = this.customMode || (useMobileStepFlow && activeTab !== tabs[tabs.length - 1]);
      }

      advanceCustomizerTab() {
        if (this.customMode) return;
        const tabs = ['frame', 'background', 'display', 'phrase'];
        const activeTab = this.querySelector('[data-custom-tab].is-active')?.dataset.customTab || tabs[0];
        const nextTab = tabs[tabs.indexOf(activeTab) + 1];
        if (nextTab) this.setCustomizerTab(nextTab);
      }

      onChange(event) {
        if (event.target.matches('[data-phrase-category-select]')) {
          this.selectCategory(event.target.value);
          return;
        }
        if (event.target.matches('[data-custom-option="1"]')) this.setCustomMode(false);
        if (event.target.matches('[data-frame-size-select]')) this.selectFrameModel(Number(event.target.value));
        if (event.target.matches('[data-wood-stand]')) this.frameChoices.woodStand = event.target.checked;
        if (event.target.matches('[data-room-select]')) this.updateRoomScene();
        this.update();
      }

      onCustomQuoteToggle(event) {
        if (!event.currentTarget.open) return;
        this.customSizeMode = true;
        this.setCustomMode(true);
        if (this.customWidth) this.customWidth.value = '';
        if (this.customHeight) this.customHeight.value = '';
        this.update();
      }

      selectCategory(category) {
        this.currentCategory = category || 'custom';
        const categorySelect = this.querySelector('[data-phrase-category-select]');
        if (categorySelect) categorySelect.value = this.currentCategory;
        this.querySelectorAll('[data-phrase-category]').forEach((button) => {
          const active = button.dataset.phraseCategory === this.currentCategory;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });

        const phraseList = this.querySelector('[data-phrase-list]');
        const custom = this.currentCategory === 'custom';
        if (phraseList) phraseList.hidden = custom;

        this.querySelectorAll('[data-phrase-card]').forEach((card) => {
          card.hidden = custom || card.dataset.category !== this.currentCategory;
        });

        if (custom) {
          this.selectedPhraseSource = 'custom';
          if (this.textField) this.textField.hidden = false;
          if (this.textInput) {
            this.textInput.value = this.customDraft;
            this.textInput.focus();
          }
          this.querySelectorAll('[data-phrase-card]').forEach((card) => card.classList.remove('is-selected'));
          this.updatePhraseProperties();
          this.update();
        }
      }

      selectPhrase(card) {
        this.selectedPhraseSource = card.dataset.source || 'suggested';
        if (this.textField) this.textField.hidden = true;
        if (this.textInput) this.textInput.value = card.dataset.phrase || '';
        this.querySelectorAll('[data-phrase-card]').forEach((item) => {
          item.classList.toggle('is-selected', item === card);
        });
        this.updatePhraseProperties();
        this.update();
      }

      updatePhraseProperties() {
        if (this.phraseCategoryProperty) {
          const categoryLabel = this.querySelector('[data-phrase-category-select]')?.selectedOptions[0]?.textContent.trim();
          this.phraseCategoryProperty.value = this.dataset.locale === 'ar' ? categoryLabel || this.currentCategory : this.currentCategory;
        }
        if (this.phraseSourceProperty) this.phraseSourceProperty.value = this.selectedPhraseSource;
      }

      setCustomMode(enabled) {
        this.customMode = Boolean(enabled);
        if (this.fixedUpload) this.fixedUpload.hidden = this.customMode;
        if (this.fixedPurchase) this.fixedPurchase.hidden = this.customMode;
        if (this.next) this.next.hidden = this.customMode;
        if (this.submit) this.submit.hidden = this.customMode;
        if (this.cartError) this.cartError.hidden = true;
        if (this.quoteForm) this.quoteForm.hidden = !this.customMode;
        this.customSizeToggle?.setAttribute('aria-pressed', String(this.customMode));
        if (this.customMode && (!this.customWidth?.value || !this.customHeight?.value)) {
          const fixed = this.dimensionsFrom(this.selectedInput(1)?.value);
          if (fixed) {
            this.customWidth.value = fixed.width;
            this.customHeight.value = fixed.height;
          }
        }
        this.update();
      }

      selectFrameModel(number) {
        const spec = this.frameSpecs.find((item) => Number(item.number) === Number(number));
        if (!spec) return;
        this.frameSpec = spec;
        this.customSizeMode = false;
        this.frameShape = this.shapeForSpec(spec);
        const dimensions = this.dimensionsFrom(spec.size);
        this.frameChoices.color = spec.colors.includes(this.frameChoices.color) ? this.frameChoices.color : spec.colors[0];
        this.frameChoices.backing = spec.backings.includes(this.frameChoices.backing) ? this.frameChoices.backing : spec.backings[0];
        this.frameChoices.paper = spec.papers.includes(this.frameChoices.paper) ? this.frameChoices.paper : spec.papers[0];
        this.frameChoices.mount = spec.mounts.includes(this.frameChoices.mount) ? this.frameChoices.mount : spec.mounts[0];
        this.frameChoices.woodStand = spec.woodStand ? this.frameChoices.woodStand : false;
        this.setCustomMode(false);
        if (dimensions) {
          if (this.customWidth) {
            this.customWidth.value = dimensions.width;
          }
          if (this.customHeight) {
            this.customHeight.value = dimensions.height;
          }
        }
        this.renderFrameModels();
        this.renderFrameSpecControls();
        this.update();
      }

      renderFrameModels() {
        const select = this.querySelector('[data-frame-size-select]');
        if (select) {
          const choices = this.frameSpecs.filter((spec) => this.shapeForSpec(spec) === this.frameShape);
          const unit = this.dataset.locale === 'en' ? 'cm' : 'سم';
          select.innerHTML = choices
            .map((spec) => {
              const dimensions = this.dimensionsFrom(spec.size);
              const direction = dimensions.width === dimensions.height ? '' : ` · ${dimensions.width > dimensions.height ? this.dataset.landscapeLabel : this.dataset.portraitLabel}`;
              return `<option value="${spec.number}"${spec === this.frameSpec ? ' selected' : ''}>${spec.size} ${unit}${direction}</option>`;
            })
            .join('');
        }
        this.querySelectorAll('[data-frame-shape]').forEach((button) => {
          const active = button.dataset.frameShape === this.frameShape;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }

      shapeForSpec(spec) {
        const dimensions = this.dimensionsFrom(spec?.size);
        return dimensions?.width === dimensions?.height ? 'square' : 'rectangle';
      }

      selectFrameShape(shape) {
        this.frameShape = shape === 'square' ? 'square' : 'rectangle';
        const first = this.frameSpecs.find((spec) => this.shapeForSpec(spec) === this.frameShape);
        if (first) this.selectFrameModel(first.number);
      }

      renderFrameSpecControls() {
        if (!this.frameSpec) return;
        const renderChoices = (selector, type, values, swatch = false) => {
          const container = this.querySelector(selector);
          if (!container) return;
          container.innerHTML = values
            .map((value) => {
              const active = this.frameChoices[type] === value;
              const isFrameColor = type === 'color';
              const style = swatch ? ` style="--mshq-swatch:${this.frameFinish(value)}"` : '';
              const sampleImage = this.frameImages[value];
              const image = isFrameColor && sampleImage
                ? `<span class="mshq-custom__frame-sample"><span class="mshq-custom__frame-sample-paper"></span><img class="mshq-custom__frame-sample-edge mshq-custom__frame-sample-edge--top" src="${sampleImage}" alt="" loading="lazy"><img class="mshq-custom__frame-sample-edge mshq-custom__frame-sample-edge--right" src="${sampleImage}" alt="" loading="lazy"><img class="mshq-custom__frame-sample-edge mshq-custom__frame-sample-edge--bottom" src="${sampleImage}" alt="" loading="lazy"><img class="mshq-custom__frame-sample-edge mshq-custom__frame-sample-edge--left" src="${sampleImage}" alt="" loading="lazy"></span>`
                : '';
              return `<button type="button" class="mshq-custom__choice${swatch ? ' mshq-custom__choice--swatch' : ''}${isFrameColor ? ' mshq-custom__choice--frame' : ''}${active ? ' is-active' : ''}" data-frame-choice="${type}" data-value="${value}"${isFrameColor ? ` data-frame-finish="${value}"` : ''} aria-pressed="${active}"${style}><i aria-hidden="true">${image}</i><span>${this.choiceLabels[value] || value}</span></button>`;
            })
            .join('');
        };
        renderChoices('[data-frame-colors]', 'color', this.frameSpec.colors.filter((value) => value !== 'maroon'), true);
        renderChoices('[data-backing-options]', 'backing', this.frameSpec.backings);
        renderChoices('[data-paper-colors]', 'paper', this.frameSpec.papers.filter((value) => value !== 'decorated' && value !== 'maroon'), true);
        renderChoices('[data-mount-options]', 'mount', this.frameSpec.mounts);
        const woodField = this.querySelector('[data-wood-stand-field]');
        const woodInput = this.querySelector('[data-wood-stand]');
        if (woodField) woodField.hidden = !this.frameSpec.woodStand;
        if (woodInput) woodInput.checked = Boolean(this.frameChoices.woodStand && this.frameSpec.woodStand);
      }

      frameFinish(value) {
        if (value === 'striped-wood') return 'repeating-linear-gradient(0deg, #321b11 0 2px, #704225 2px 7px, #4b2a18 7px 9px)';
        return this.valueColor(value, '#e8d5a9');
      }

      frameTexture(value) {
        if (value === 'wood-brown') return 'linear-gradient(135deg, rgba(130, 57, 36, 0.72), transparent 34%, rgba(35, 12, 9, 0.5) 76%), repeating-linear-gradient(90deg, rgba(255, 190, 143, 0.08) 0 1px, transparent 1px 8px)';
        if (value === 'light-wood') return 'linear-gradient(135deg, rgba(255, 220, 175, 0.5), transparent 42%, rgba(104, 55, 28, 0.16)), repeating-linear-gradient(90deg, rgba(100, 49, 24, 0.12) 0 1px, transparent 1px 9px)';
        if (value === 'black') return 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent 35%, rgba(0, 0, 0, 0.42))';
        if (value === 'white') return 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), transparent 52%, rgba(80, 68, 55, 0.12))';
        return 'none';
      }

      currentDimensions() {
        if (this.customMode) {
          const width = Number(this.customWidth?.value);
          const height = Number(this.customHeight?.value);
          return width > 0 && height > 0 ? { width, height } : null;
        }
        if (this.frameSpec) return this.dimensionsFrom(this.frameSpec.size);
        return this.dimensionsFrom(this.selectedInput(1)?.value);
      }

      currentSizeLabel() {
        if (this.customMode) {
          const dimensions = this.currentDimensions();
          return dimensions ? `${dimensions.width} × ${dimensions.height} cm` : '';
        }
        if (this.frameSpec) return `${this.frameSpec.size} cm`;
        return this.selectedInput(1)?.value || '';
      }

      currentRule() {
        if (this.frameSpec) {
          return {
            maxCharacters: 200,
            maxWords: Number(this.frameSpec.maxWords) || 8,
            maxLines: 4,
            guidance: `الحد الأقصى لهذا الفريم هو ${this.frameSpec.maxWords} كلمات.`,
          };
        }
        const size = this.normalizeSize(this.currentSizeLabel());
        const exact = this.sizeRules.find((rule) => rule.size === size);
        if (exact) return exact;

        const dimensions = this.currentDimensions();
        const longest = dimensions ? Math.max(dimensions.width, dimensions.height) : 0;
        if (longest <= 30) return this.defaultRules[0];
        if (longest <= 40) return this.defaultRules[1];
        if (longest <= 50) return this.defaultRules[2];
        return this.defaultRules[3];
      }

      countGraphemes(value) {
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
          const segmenter = new Intl.Segmenter(this.dataset.locale || 'en', { granularity: 'grapheme' });
          return [...segmenter.segment(value)].length;
        }
        return Array.from(value).length;
      }

      textMetrics() {
        const text = this.textInput?.value || '';
        const normalized = text.trim();
        return {
          characters: this.countGraphemes(text),
          words: normalized ? normalized.split(/\s+/u).length : 0,
          lines: text ? text.split(/\r?\n/u).length : 0,
        };
      }

      validateText() {
        if (!this.textInput) return true;
        const rule = this.currentRule();
        const metrics = this.textMetrics();
        let message = '';
        if (!this.textInput.value.trim()) message = this.dataset.textRequired || 'Enter the requested text.';
        else if (!this.frameSpec && !this.customMode && metrics.characters > rule.maxCharacters)
          message = `${this.dataset.textTooLong || 'Text is too long.'} (${rule.maxCharacters})`;
        else if ((!this.customMode || this.frameSpec) && metrics.words > rule.maxWords)
          message = `${this.dataset.textTooManyWords || 'Too many words.'} (${rule.maxWords})`;
        else if (!this.frameSpec && !this.customMode && metrics.lines > rule.maxLines)
          message = `${this.dataset.textTooManyLines || 'Too many lines.'} (${rule.maxLines})`;

        this.textInput.setCustomValidity(message);
        this.textInput.setAttribute('aria-invalid', String(Boolean(message)));
        if (this.textError) {
          this.textError.textContent = message || rule.guidance || '';
          this.textError.hidden = !message && !rule.guidance;
        }

        this.querySelector('[data-character-counter]')?.replaceChildren(
          document.createTextNode(
            `${metrics.characters}/${rule.maxCharacters} ${this.dataset.charactersLabel || 'characters'}`,
          ),
        );
        this.querySelector('[data-word-counter]')?.replaceChildren(
          document.createTextNode(`${metrics.words}/${rule.maxWords} ${this.dataset.wordsLabel || 'words'}`),
        );
        this.querySelector('[data-line-counter]')?.replaceChildren(
          document.createTextNode(`${metrics.lines}/${rule.maxLines} ${this.dataset.linesLabel || 'lines'}`),
        );
        return !message;
      }

      updatePhraseAvailability() {
        const dimensions = this.currentDimensions();
        const longest = dimensions ? Math.max(dimensions.width, dimensions.height) : 0;
        this.querySelectorAll('[data-phrase-card]').forEach((card) => {
          const required = this.dimensionsFrom(card.dataset.minimumSize);
          const unavailable = Boolean(required && longest < Math.max(required.width, required.height));
          card.setAttribute('aria-disabled', String(unavailable));
        });
      }

      valueColor(value, fallback) {
        const normalized = String(value || '')
          .trim()
          .toLowerCase();
        if (/^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
        return this.colorMap[normalized] || fallback;
      }

      formatDelta(cents) {
        if (!cents) return '';
        const formatted = this.formatPrice(Math.abs(cents));
        return `${cents > 0 ? '+' : '−'}${formatted}`;
      }

      updateOptionCards() {
        [
          { position: 1, selector: '[data-size-card]' },
          { position: 2, selector: '[data-frame-card]' },
        ].forEach(({ position, selector }) => {
          const selected = [];
          this.optionInputs.forEach((input) => {
            if (input.checked) selected[Number(input.dataset.customOption) - 1] = input.value;
          });
          const candidates = this.variants.filter((variant) =>
            variant.options.every(
              (value, index) => index === position - 1 || !selected[index] || value === selected[index],
            ),
          );
          const available = candidates.filter((variant) => variant.available);
          const priced = available.length ? available : candidates;
          const baseline = priced.length ? Math.min(...priced.map((variant) => Number(variant.price))) : 0;

          this.querySelectorAll(selector).forEach((card) => {
            const variant = candidates.find((item) => item.options[position - 1] === card.dataset.optionValue);
            const input = card.closest('label')?.querySelector('input');
            const unavailable = !variant?.available;
            if (input) input.disabled = unavailable;
            card.closest('label')?.classList.toggle('is-unavailable', unavailable);
            const delta = card.querySelector('[data-option-price-delta]');
            if (delta) delta.textContent = variant ? this.formatDelta(Number(variant.price) - baseline) : '';
          });
        });

        this.querySelectorAll('.mshq-custom__frame-option').forEach((label) => {
          const categories = String(label.dataset.recommendedCategories || '')
            .split('|')
            .map((value) => value.trim().toLowerCase());
          const recommended = this.currentCategory !== 'custom' && categories.includes(this.currentCategory);
          label.classList.toggle('is-recommended', recommended);
          const badge = label.querySelector('[data-recommended-badge]');
          if (badge) badge.hidden = !recommended;
        });
      }

      updateVariant() {
        const variant = this.selectedPricingVariant();
        if (!variant) {
          if (this.submit) {
            this.submit.disabled = true;
            this.submit.querySelector('span')?.replaceChildren(document.createTextNode(this.dataset.unavailable));
          }
          return;
        }
        if (this.variantId) this.variantId.value = variant.id;
        if (this.price) this.price.textContent = this.formatPrice(this.frameSpec?.price ? this.frameSpec.price * 100 : variant.price);
        if (this.submit)
          this.submit.disabled = !variant.available || this.submit.dataset.loading === 'true' || !this.validateText();
      }

      formatPrice(cents) {
        if (this.dataset.locale === 'ar') {
          const amount = Number(cents || 0) / 100;
          return `${new Intl.NumberFormat('ar-QA-u-nu-arab', {
            useGrouping: false,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount).replace(/٫/g, ',')} ر.ق`;
        }
        try {
          return new Intl.NumberFormat(this.dataset.locale === 'ar' ? 'ar-QA-u-nu-arab' : (this.dataset.locale || 'en-QA'), {
            style: 'currency',
            currency: this.dataset.currencyCode || 'QAR',
          }).format(Number(cents || 0) / 100);
        } catch (_error) {
          return `${(Number(cents || 0) / 100).toLocaleString(this.dataset.locale === 'ar' ? 'ar-QA-u-nu-arab' : 'en-QA')} ${this.dataset.locale === 'ar' ? 'ر.ق' : (this.dataset.currencyCode || 'QAR')}`;
        }
      }

      updateFramePreview() {
        const dimensions = this.currentDimensions();
        const usingCustomChoices = this.frameSpec || this.customSizeMode;
        const backgroundValue = usingCustomChoices ? this.frameChoices.paper : this.selectedInput(3)?.value;
        const frameValue = usingCustomChoices ? this.frameChoices.color : this.selectedInput(2)?.value;
        const background = frameValue === 'white' && backgroundValue === 'white' ? '#e8d5a9' : this.valueColor(backgroundValue, '#e8d5a9');
        const frameColor = frameValue === 'white' ? '#ffffff' : this.valueColor(frameValue, '#5b3a24');
        const frameTexture = this.frameTexture(frameValue);
        const frameImage = usingCustomChoices ? this.frameImages[frameValue] : null;
        const previewTextureKey = frameValue === 'wood-brown' ? 'striped-wood' : frameValue;
        const frameTextureImage = usingCustomChoices && !['light-wood', 'striped-wood', 'white'].includes(frameValue) ? this.frameTextures[previewTextureKey] : null;
        const frameMaterial = frameValue === 'white' ? null : frameTextureImage || frameImage;
        const textColor = background === '#1b1b1b' ? '#f5f1e6' : '#201f1c';
        const text = this.textInput?.value || this.preview?.dataset.placeholder || '';
        const decorated = backgroundValue === 'decorated';

        if (this.preview) this.preview.textContent = text || '…';
        if (this.roomText) this.roomText.textContent = text || '…';
        if (this.paper) this.paper.style.backgroundColor = background;
        if (this.roomPaper) this.roomPaper.style.backgroundColor = background;
        [this.frame, this.roomFrame].forEach((node) => {
          if (!node) return;
          node.dataset.frameColor = frameValue || '';
          node.dataset.frameImage = frameMaterial ? 'true' : 'false';
          node.dataset.frameMaterial = frameTextureImage ? 'texture' : 'profile';
          node.style.setProperty('--frame-color', frameColor);
          node.style.setProperty('--frame-texture', frameTexture);
          if (frameMaterial) {
            node.style.setProperty('--frame-image', `url("${frameMaterial}")`);
          } else {
            node.style.removeProperty('--frame-image');
          }
          node.style.background = '';
          if (frameValue === 'white' || frameValue === 'gold') {
            node.style.removeProperty('background-color');
            node.style.setProperty('background-image', 'none');
          } else {
            node.style.removeProperty('background-color');
            node.style.removeProperty('background-image');
          }
          node.style.setProperty('--mshq-custom-frame-thickness', '1.35rem');
          node.dataset.backing = this.frameSpec ? this.frameChoices.backing : 'paper';
        });
        if (this.decoratedPreview) this.decoratedPreview.hidden = !decorated;
        if (this.paper) this.paper.hidden = decorated;
        if (this.frame) {
          this.frame.dataset.decorated = String(decorated);
          this.frame.dataset.frameColor = frameValue || '';
          this.frame.style.padding = '';
        }
        if (this.decoratedPreviewText) {
          this.decoratedPreviewText.textContent = text;
          const length = Math.max(1, text.length);
          this.decoratedPreviewText.style.fontSize = `${Math.max(0.7, Math.min(2.2, 5 / Math.sqrt(length)))}rem`;
        }
        if (this.preview) this.preview.style.color = textColor;
        if (this.roomText) this.roomText.style.color = textColor;
        this.fitPreviewText(this.preview, dimensions);
        this.fitPreviewText(this.roomText, dimensions);
        this.fitPreviewText(this.decoratedPreviewText, dimensions);
        if (this.previewWrap) this.previewWrap.dataset.frameColor = frameValue || '';
        if (this.sizePreview) this.sizePreview.textContent = this.currentSizeLabel();

        if (dimensions) {
          [this.frame, this.roomFrame].forEach((node) =>
            node?.style.setProperty('--mshq-custom-frame-ratio', `${dimensions.width} / ${dimensions.height}`),
          );
          const measuredSides = this.frameSpec
            ? this.frameSpecs.map((spec) => this.dimensionsFrom(spec.size)).filter(Boolean).map((size) => Math.max(size.width, size.height))
            : this.sizeInputs
            .map((input) => this.dimensionsFrom(input.value))
            .filter(Boolean)
            .map((size) => Math.max(size.width, size.height));
          const maxSide = Math.max(...measuredSides, Math.max(dimensions.width, dimensions.height));
          const widthPercent = Math.max(
            50,
            Math.min(78, (Math.max(dimensions.width, dimensions.height) / maxSide) * 100),
          );
          this.frame?.style.setProperty('--mshq-custom-frame-width', `${widthPercent}%`);
        }
      }

      fitPreviewText(node, dimensions) {
        if (!node || node.closest('[hidden]')) return;

        const container = node.parentElement;
        if (!container) return;

        // Short landscape sizes have very little vertical space. Start with a
        // size based on the actual rendered frame, then shrink only when the
        // rendered Arabic text would overflow its paper area.
        requestAnimationFrame(() => {
          if (!node.isConnected || node.closest('[hidden]')) return;
          const width = container.clientWidth;
          const height = container.clientHeight;
          if (!width || !height) return;

          const length = Math.max(1, [...(node.textContent || '')].length);
          // Scale from the real rendered area and phrase length. This avoids
          // oversized Arabic glyphs in compact portrait/landscape sizes before
          // the browser has finished calculating line wrapping.
          const physicalScale = dimensions
            ? Math.min(1.15, Math.max(0.72, Math.sqrt((dimensions.width * dimensions.height) / (60 * 30))))
            : 1;
          const isDecoratedText = node === this.decoratedPreviewText;
          let fontSize = Math.min(
            22,
            Math.max(8, 145 / Math.sqrt(length)) * physicalScale * (isDecoratedText ? 0.68 : 1),
            height * (isDecoratedText ? 0.13 : 0.14),
            width * (isDecoratedText ? 0.055 : 0.08),
          );
          node.style.fontSize = `${fontSize}px`;
          node.style.lineHeight = '1.28';

          for (let attempt = 0; attempt < 24; attempt += 1) {
            const overflows = node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1;
            if (!overflows || fontSize <= 9) break;
            fontSize *= 0.9;
            node.style.fontSize = `${fontSize}px`;
          }
        });
      }

      updatePersonScene() {
        const size = this.normalizeSize(this.currentSizeLabel());
        let found = false;
        this.querySelectorAll('[data-person-scene]').forEach((scene) => {
          const matches = !this.customMode && this.normalizeSize(scene.dataset.size) === size;
          scene.hidden = !matches;
          if (matches) found = true;
        });
        const empty = this.querySelector('[data-person-scene-empty]');
        if (empty) empty.hidden = found;
      }

      initializeRoom() {
        const select = this.querySelector('[data-room-select]');
        if (select && select.options.length) select.selectedIndex = 0;
        this.updateRoomScene();
      }

      updateRoomScene() {
        const select = this.querySelector('[data-room-select]');
        const option = select?.selectedOptions?.[0];
        const image = this.querySelector('[data-room-image]');
        const empty = this.querySelector('[data-room-empty]');
        if (!option?.dataset.image) {
          if (image) image.removeAttribute('src');
          if (empty) empty.hidden = false;
          return;
        }
        if (image) image.src = option.dataset.image;
        if (empty) empty.hidden = true;

        const dimensions = this.currentDimensions();
        const wallWidth = Number(option.dataset.wallWidth) || 300;
        const widthPercent = dimensions ? Math.max(5, Math.min(70, (dimensions.width / wallWidth) * 100)) : 20;
        this.roomFrame?.style.setProperty('--mshq-room-width', `${widthPercent}%`);
        this.roomFrame?.style.setProperty('--mshq-room-x', `${Number(option.dataset.anchorX) || 50}%`);
        this.roomFrame?.style.setProperty('--mshq-room-y', `${Number(option.dataset.anchorY) || 45}%`);
      }

      selectPreview(mode) {
        this.querySelectorAll('[data-preview-tab]').forEach((button) => {
          const active = button.dataset.previewTab === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        this.querySelectorAll('[data-preview-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.previewPanel !== mode;
        });
        if (mode === 'person') this.updatePersonScene();
        if (mode === 'room') this.updateRoomScene();
      }

      updateOrientation() {
        const orientation = this.querySelector('[data-custom-orientation]');
        const dimensions = this.currentDimensions();
        if (!orientation) return;
        if (!dimensions) orientation.textContent = '';
        else if (dimensions.width === dimensions.height) orientation.textContent = this.dataset.squareLabel || 'Square';
        else
          orientation.textContent =
            dimensions.width > dimensions.height
              ? this.dataset.landscapeLabel || 'Landscape'
              : this.dataset.portraitLabel || 'Portrait';
      }

      update() {
        this.updatePhraseProperties();
        const validText = this.validateText();
        this.updateOptionCards();
        this.updateVariant();
        if (!this.customMode && this.submit && !validText) this.submit.disabled = true;
        this.updatePhraseAvailability();
        this.updateFramePreview();
        this.updatePersonScene();
        this.updateRoomScene();
        this.updateOrientation();
        this.syncQuoteFields();
      }

      syncQuoteFields() {
        const unit = this.dataset.locale === 'en' ? 'cm' : 'سم';
        const frameLabel = this.dataset.locale === 'en' ? 'Frame' : 'فريم';
        const customSizeLabel = this.dataset.locale === 'en' ? 'Custom size' : 'مقاس خاص';
        const assignments = [
          ['[data-quote-text]', this.textInput?.value || ''],
          ['[data-quote-category]', this.currentCategory],
          ['[data-quote-frame]', this.customSizeMode ? customSizeLabel : this.frameSpec ? `${frameLabel} ${this.frameSpec.number} (${this.frameSpec.size} ${unit})` : this.selectedInput(2)?.value || ''],
          ['[data-quote-background]', this.frameSpec || this.customSizeMode ? this.choiceLabels[this.frameChoices.paper] : this.selectedInput(3)?.value || ''],
          ['[data-quote-model]', this.customSizeMode ? customSizeLabel : this.frameSpec ? `${frameLabel} ${this.frameSpec.number} — ${this.frameSpec.size} ${unit}` : ''],
          ['[data-quote-profile]', this.customSizeMode ? (this.dataset.locale === 'en' ? 'Custom' : 'مخصص') : this.frameSpec?.profile || ''],
          ['[data-quote-colour]', this.choiceLabels[this.frameChoices.color] || ''],
          ['[data-quote-backing]', this.choiceLabels[this.frameChoices.backing] || ''],
          ['[data-quote-paper-colour]', this.choiceLabels[this.frameChoices.paper] || ''],
          ['[data-quote-mount]', this.choiceLabels[this.frameChoices.mount] || ''],
          ['[data-quote-wood-stand]', this.frameChoices.woodStand ? (this.dataset.locale === 'en' ? 'Yes' : 'نعم') : (this.dataset.locale === 'en' ? 'No' : 'لا')],
          ['[data-quote-word-limit]', this.frameSpec ? String(this.frameSpec.maxWords) : ''],
        ];
        assignments.forEach(([selector, value]) => {
          const input = this.querySelector(selector);
          if (input) input.value = value;
        });
        if (this.frameColorProperty) this.frameColorProperty.value = this.choiceLabels[this.frameChoices.color] || '';
        const shapeLabel = this.frameShape === 'square'
          ? (this.dataset.locale === 'ar' ? 'مربع' : 'Square')
          : (this.dataset.locale === 'ar' ? 'مستطيل' : 'Rectangle');
        const dimensions = this.currentDimensions();
        const sizeLabel = dimensions
          ? `${dimensions.width}×${dimensions.height} ${unit}`
          : '';
        const customAssignments = [
          ['[data-custom-shape-property]', shapeLabel],
          ['[data-custom-size-property]', sizeLabel],
          ['[data-custom-backing-property]', this.choiceLabels[this.frameChoices.backing] || ''],
          ['[data-custom-paper-property]', this.choiceLabels[this.frameChoices.paper] || ''],
          ['[data-custom-mount-property]', this.choiceLabels[this.frameChoices.mount] || ''],
        ];
        customAssignments.forEach(([selector, value]) => {
          const input = this.querySelector(selector);
          if (input) input.value = value;
        });
      }

      prepareQuote(event) {
        this.syncQuoteFields();
        const width = Number(this.customWidth?.value);
        const height = Number(this.customHeight?.value);
        const min = Number(this.dataset.minDimension) || 15;
        const max = Number(this.dataset.maxDimension) || 120;
        const validDimensions = width >= min && width <= max && height >= min && height <= max;
        if (!validDimensions || !this.textInput?.value.trim()) {
          event.preventDefault();
          if (!validDimensions) this.customWidth?.reportValidity();
          else this.textInput?.reportValidity();
        }
      }

      async generateAiPreview() {
        const visualizer = this.querySelector('[data-ai-visualizer]');
        const file = visualizer?.querySelector('[data-ai-file]')?.files?.[0];
        const consent = visualizer?.querySelector('[data-ai-consent]')?.checked;
        const button = visualizer?.querySelector('[data-ai-submit]');
        const status = visualizer?.querySelector('[data-ai-status]');
        const resultImage = visualizer?.querySelector('[data-ai-result]');
        if (!visualizer || !file || !consent || !button || file.size > 10 * 1024 * 1024) {
          if (status) status.textContent = this.dataset.aiError || 'Choose a valid room image and confirm consent.';
          return;
        }

        const dimensions = this.currentDimensions();
        const variant = this.selectedPricingVariant();
        const payload = new FormData();
        payload.append('room_image', file);
        payload.append('variant_id', String(variant?.id || ''));
        payload.append('requested_text', this.textInput?.value || '');
        payload.append('size_width_cm', String(dimensions?.width || ''));
        payload.append('size_height_cm', String(dimensions?.height || ''));
        payload.append('frame_style', this.selectedInput(2)?.value || '');

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (status) status.textContent = this.dataset.aiProcessing || 'Creating your preview…';
        if (resultImage) resultImage.hidden = true;

        try {
          const response = await fetch(visualizer.dataset.aiEndpoint, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: payload,
          });
          if (!response.ok) throw new Error('AI request failed');
          const job = await response.json();
          const completed = job.image_url ? job : await this.pollAiPreview(visualizer.dataset.aiEndpoint, job.job_id);
          if (!completed?.image_url) throw new Error('AI result missing');
          if (resultImage) {
            resultImage.src = completed.image_url;
            resultImage.hidden = false;
          }
          if (status) status.textContent = '';
        } catch (_error) {
          if (status) status.textContent = this.dataset.aiError || 'The AI preview is unavailable.';
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }

      async pollAiPreview(endpoint, jobId) {
        if (!jobId) return null;
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 1500));
          const response = await fetch(`${endpoint}/${encodeURIComponent(jobId)}`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('AI polling failed');
          const job = await response.json();
          if (job.status === 'failed') throw new Error('AI generation failed');
          if (job.status === 'complete' || job.image_url) return job;
        }
        throw new Error('AI generation timed out');
      }

      showCartError(message = '') {
        if (!this.cartError) return;
        this.cartError.textContent = message;
        this.cartError.hidden = !message;
      }

      async addToCart(event) {
        event.preventDefault();
        if (this.customMode || !this.fixedForm || !this.submit || this.submit.dataset.loading === 'true') return;
        if (!this.fixedForm.reportValidity() || !this.validateText()) return;

        const drawer = document.querySelector('cart-drawer');
        const hasDrawer =
          drawer &&
          typeof drawer.getSectionsToRender === 'function' &&
          typeof drawer.renderContents === 'function';

        this.submit.dataset.loading = 'true';
        this.submit.disabled = true;
        this.submit.setAttribute('aria-busy', 'true');
        this.showCartError();
        const formData = new FormData(this.fixedForm);
        if (hasDrawer) {
          formData.append(
            'sections',
            drawer
              .getSectionsToRender()
              .map((item) => item.id)
              .join(','),
          );
          formData.append('sections_url', window.location.pathname);
          drawer.setActiveElement?.(this.submit);
        }

        try {
          const cartAddUrl = String(window.routes?.cart_add_url || '/cart/add').endsWith('.js')
            ? window.routes.cart_add_url
            : `${window.routes?.cart_add_url || '/cart/add'}.js`;
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message);
          if (hasDrawer) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(result);
          } else {
            window.location.href = window.routes.cart_url || '/cart';
          }
        } catch (error) {
          this.showCartError(error.message || this.dataset.cartError || window.cartStrings?.error);
        } finally {
          this.submit.dataset.loading = 'false';
          this.submit.removeAttribute('aria-busy');
          this.updateVariant();
        }
      }
    },
  );
}
